import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { getSmallBusinessIndustries, getMediumBusinessIndustries } from '../data/industriesData';

const smallBusiness = getSmallBusinessIndustries();
const mediumBusiness = getMediumBusinessIndustries();

const tabs = [
  { id: 'small', label: 'Local Business', data: smallBusiness },
  { id: 'medium', label: 'Mid-Size Business', data: mediumBusiness }
];

const IndustriesSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation(0.2);
  const [activeTab, setActiveTab] = useState('small');
  const activeData = tabs.find(t => t.id === activeTab).data;
  const navigate = useNavigate();

  return (
    <section id="industries" className="relative py-20 bg-[#0A0A0A] overflow-hidden">
      {/* Decorative orange orbs */}
      <div className="orange-orb orange-orb-1 top-1/4 -left-32 w-96 h-96" />
      <div className="orange-orb orange-orb-2 bottom-1/4 -right-32 w-[28rem] h-[28rem]" />

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 40 }}
          animate={headerVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-10"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FF6B00]/10 border border-[#FF6B00]/30 text-[10px] font-mono-tech uppercase tracking-[0.25em] text-[#FF6B00] mb-5">
            Industries
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-3">
            Built for your industry.
          </h2>
          <p className="text-[#A1A1AA] text-sm md:text-base max-w-xl mx-auto">
            Select your category to see specific use cases.
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex justify-center gap-2 mb-10" data-testid="industries-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`industries-tab-${tab.id}`}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-[#FF6B00] text-white shadow-[0_0_40px_rgba(255,107,0,0.4)]'
                  : 'bg-white/[0.05] text-[#A1A1AA] hover:bg-white/[0.08] hover:text-black border border-white/10'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Industry Cards */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {activeData.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <motion.div
                  key={industry.slug}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  onClick={() => navigate(`/industries/${industry.slug}`)}
                  className="group cursor-pointer bg-[#0A0A0A] rounded-2xl border border-white/[0.08] p-6 hover:shadow-[0_8px_32px_rgba(0,0,0,0.45)] hover:border-white/10 transition-all duration-300 card-glow-hover"
                  data-testid={`industry-card-${index}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-xl bg-[#FF6B00]/15 border border-[#FF6B00]/25 group-hover:bg-[#FF6B00]/30 group-hover:border-[#FF6B00]/50 flex items-center justify-center transition-all duration-300">
                      <Icon className="w-5 h-5 text-[#FF6B00] transition-colors duration-300" />
                    </div>
                    <h3 className="font-bold text-white">{industry.title}</h3>
                  </div>
                  <p className="text-sm text-[#A1A1AA] leading-relaxed mb-3">{industry.tagline}</p>
                  <div className="flex items-center gap-1.5 text-xs font-medium text-white">
                    See how we help
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default IndustriesSection;
